import React, { useContext, useMemo, useState } from "react";
import GroupForm from "../../../../../components/GroupForm/GroupForm";
import { TreeNode } from "../../../../../components/TreeCheckboxGroup";
import { sessionStore } from "../../../../../contexts/SessionContext";
import { prepareClassroomsForTreeCheckboxGroup } from "../../../Playlist/Teacher/EditPlaylist/utils";
import { defineMessages, useIntl } from "react-intl";
import { useUrl } from "../../../../../hooks/usePage";
import { Pagetype } from "../../../../../interfaces/Config";
import {
    useCreatePadMutation,
    usePostPadAssignment,
} from "../api/useMutations";
import { useUserId } from "../../../../../hooks/useUserInfo";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { toast } from "react-toastify";
import PadCreationToast from "./PadCreationToast";
import { useWorkshops } from "../../useWorkshops";
import { useGetPadUrl } from "../api/useQueries";
import { CreatedPad, WorkshopPadAssignment } from "../../types";
import Loader from "../../../../../components/Loader/Loader";

const PadGroupForm = () => {
    const history = useHistory();
    const location = useLocation<{ from?: string }>();
    const userId = useUserId();
    const {
        session: { classrooms },
    } = useContext(sessionStore);
    const intl = useIntl();

    const students = useMemo<Record<string, TreeNode>>(
        () => prepareClassroomsForTreeCheckboxGroup(classrooms),
        [classrooms]
    );
    const workshopsUrl = useUrl(Pagetype.WORKMODE_WORKSHOP);
    const { workshopId } = useParams<{ workshopId: string }>();
    const workshops = useWorkshops();
    const workshopName = workshops.data.find(
        (collection) => collection.id === workshopId
    )?.name;

    const { mutateAsync: createPad } = useCreatePadMutation();
    const { mutateAsync: createPadAssignement } = usePostPadAssignment();

    const [pad, setPad] =
        useState<Omit<WorkshopPadAssignment, "students" | "extra">>();
    const padUrl = useGetPadUrl(pad?.pad_id);
    const [isLoading, setIsLoading] = useState(false);

    const backTo = location.state?.from ?? workshopsUrl;

    const createNewPad = async (name: string, students: string[]) => {
        setIsLoading(true);
        try {
            const data: CreatedPad = await createPad({
                pad_name: name,
                html: "",
            });
            const newPad = {
                pad_name: name,
                pad_id: data.pad_id,
                students: students,
                workshop_id: workshopId,
                assigned_resource: {
                    resource_id: data.pad_id,
                    resource_type: "workshop" as const,
                },
                teacher: userId,
            };

            setPad(newPad);
            assignPad(students, newPad);
        } catch (err) {
            setIsLoading(false);
            toast(
                <PadCreationToast
                    padName={name}
                    eventType="error"
                    toastPadTitle={messages.toastPadEventCreationError}
                />,
                {
                    containerId: "progress-notifier",
                    autoClose: 8000,
                    closeOnClick: false,
                    type: "error",
                    icon: (
                        <Icon
                            path="wrong"
                            size="medium"
                            color="var(--color-error)"
                        />
                    ),
                }
            );
        }
    };

    const assignPad = (
        students: string[],
        pad: Omit<WorkshopPadAssignment, "students" | "extra">
    ) => {
        if (!pad || !pad?.pad_id) return;

        createPadAssignement(
            {
                ...pad,
                extra: {
                    pad_url: padUrl.data,
                    workshop_id: workshopId,
                },
                students: students,
            },
            {
                onSuccess: () => {
                    toast(
                        <PadCreationToast
                            padName={pad.pad_name}
                            padId={pad.pad_id!}
                            workshopId={workshopId}
                            toastPadTitle={messages.toastPadEventSuccess}
                            eventType="success"
                        />,
                        {
                            containerId: "progress-notifier",
                            autoClose: 8000,
                            closeOnClick: false,
                            type: "success",
                            icon: (
                                <Icon
                                    path="valid"
                                    size="medium"
                                    color="green"
                                />
                            ),
                        }
                    );
                    history.push(workshopsUrl);
                },
                onError: (error) => {
                    console.log(error);
                    toast(
                        <PadCreationToast
                            padName={pad.pad_name}
                            padId={pad.pad_id!}
                            workshopId={workshopId}
                            eventType="error"
                            toastPadTitle={messages.toastPadEventSendingError}
                        />,
                        {
                            containerId: "progress-notifier",
                            autoClose: 8000,
                            closeOnClick: false,
                            type: "error",
                            icon: (
                                <Icon
                                    path="wrong"
                                    size="medium"
                                    color="var(--color-error)"
                                />
                            ),
                        }
                    );
                },
            }
        );
    };

    if (isLoading) return <Loader isLoading />;

    return (
        <GroupForm
            students={students}
            onSubmit={(args) => {
                if (students)
                    createNewPad(
                        args.name,
                        args.students?.map(
                            (studentId: string) => studentId.split("/")[1]
                        )
                    );
            }}
            defaultValues={{
                title: intl.formatMessage(messages.newPad, {
                    workshop: workshopName,
                }),
                students: [],
                name: intl.formatMessage(messages.newPadPlaceholder),
            }}
            messages={padGroupFormMessages}
            goBackUrl={backTo}
        />
    );
};

export default PadGroupForm;

const messages = defineMessages({
    title: {
        id: "workshop-pad-form-title",
        defaultMessage: "Create a new pad.",
    },
    classroomListDescription: {
        id: "workshop-pad-form-classroomListDescription",
        defaultMessage: "Select the students you want to add to your new pad.",
    },
    newPad: {
        id: "workshop-pad-form-newPad",
        defaultMessage: "New pad for : {workshop}",
    },
    newPadPlaceholder: {
        id: "workshop-pad-form-newPad-placeholder",
        defaultMessage: "New pad name",
    },
    groupName: {
        id: "workshop-pad-form-padName",
        defaultMessage: "Pad name",
    },
    groupNameInstruction: {
        id: "workshop-pad-form-padNameInstruction",
        defaultMessage: "Enter the name of the pad",
    },
    submit: {
        id: "workshop-pad-form-submit",
        defaultMessage: "Send the pad to my students",
    },
    goBackToWorkshopsList: {
        id: "workshop-pad-form-GoBackToWorkshopList",
        defaultMessage: "Go back to the workshop list",
    },
    toastPadEventSuccess: {
        id: "workshop-pad-toast-title",
        defaultMessage: "The pad {padName} was successfully created.",
    },
    toastPadEventCreationError: {
        id: "workshop-pad-toast-creation-error",
        defaultMessage:
            "We could not create the pad {padName}. If you have alreay a pad with this name, you have to choose another one.",
    },
    toastPadEventSendingError: {
        id: "workshop-pad-toast-sending-error",
        defaultMessage:
            "We could not send the pad {padName} to the selected students. Please try later.",
    },
});

const padGroupFormMessages = {
    description: messages.classroomListDescription,
    submit: messages.submit,
    backTo: messages.goBackToWorkshopsList,
    groupName: messages.groupName,
    groupNameInstruction: messages.groupNameInstruction,
};
